import { useChat } from "@ai-sdk/react";
import type { UIMessage } from "ai";
import { Check, Loader2, Send, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { useAuthToken } from "@/hooks/use-auth-token";
import {
  createToolAwareTransport,
  type ToolStatusEvent,
} from "@/lib/ai/tool-aware-transport";
import type { EffectCard as EffectCardData } from "@/lib/core/schemas/effect-card";
import { ChatSDKError } from "@/lib/errors";
import logger from "@/lib/logger";
import { cn } from "@/lib/utils";
import { ActionRollCard } from "./action-roll-card";
import { EffectCard } from "./effect-card";
import { type EncounterDomain, useGameState } from "./game-state-context";
import { InitiativeHeader } from "./initiative-header";
import { InitiativeRollCard } from "./initiative-roll-card";
import { extractTextFromParts, messageText } from "./message-text";
import { PendingChoiceCard } from "./pending-choice-card";
import { SaveRollCard } from "./save-roll-card";
import { SkillCheckAnimationProvider } from "./skill-check-animation-context";
import { SkillCheckCard } from "./skill-check-card";

const TOOL_PART_PREFIX_RE = /^tool-/;

/**
 * Distance (in pixels) from the bottom of the chat viewport within which
 * we consider the user "pinned" and continue auto-scrolling on new messages.
 * Matches the Slack / Discord / iMessage pattern. See
 * docs/superpowers/specs/2026-04-10-chat-scroll-respect-user-design.md.
 */
const AUTOSCROLL_THRESHOLD_PX = 100;

const toolNameMapping: Record<string, string> = {
  search_compendium: "Searching compendium",
  spawn_entity_instance: "Spawning entity instance",
};

function formatToolName(name: string): string {
  if (toolNameMapping[name]) {
    return toolNameMapping[name];
  }
  return name.replace(/_/g, " ");
}

/**
 * Type guard for tool invocation parts produced by the AI SDK's
 * UIMessage stream protocol (tool-input-available / tool-output-available).
 */
export type ToolInvocationPart = {
  type: string;
  toolCallId: string;
  state: string;
  input?: unknown;
  output?: unknown;
  errorText?: string;
  toolName?: string;
};

/** UIMessage with `parts` widened to `unknown[]` so our type-guard filter narrows correctly. */
type UIMessageWithParts = Omit<UIMessage, "parts"> & { parts?: unknown[] };

function isToolPart(part: unknown): part is ToolInvocationPart {
  if (part === null || typeof part !== "object") {
    return false;
  }
  const p = part as Record<string, unknown>;
  if (
    typeof p.type !== "string" ||
    !(p.type.startsWith("tool-") || p.type === "dynamic-tool") ||
    typeof p.toolCallId !== "string" ||
    typeof p.state !== "string"
  ) {
    return false;
  }

  if (p.type === "dynamic-tool") {
    return typeof p.toolName === "string";
  }

  return true;
}

/** Extract the bare tool name from a tool part. */
function toolPartName(part: ToolInvocationPart): string {
  // toolName is the canonical source — available on all part types in AI SDK v6
  if (part.toolName) {
    return part.toolName;
  }
  // Fallback: strip "tool-" prefix from type (legacy)
  return part.type.replace(TOOL_PART_PREFIX_RE, "");
}

/** Extract skillCheckId from a forge_skill_check tool part's output. */
function extractSkillCheckId(tp: ToolInvocationPart): string | null {
  if (tp.state !== "output-available" || tp.output === undefined) {
    return null;
  }
  try {
    const parsed =
      typeof tp.output === "string" ? JSON.parse(tp.output) : tp.output;
    if (parsed && typeof parsed === "object") {
      // The tool result may be nested as { data: { skillCheckId } } or flat { skillCheckId }
      const obj = parsed as Record<string, unknown>;
      if (typeof obj.skillCheckId === "string") {
        return obj.skillCheckId;
      }
      if (obj.data && typeof obj.data === "object") {
        const data = obj.data as Record<string, unknown>;
        if (typeof data.skillCheckId === "string") {
          return data.skillCheckId;
        }
      }
    }
  } catch {
    // Output isn't parseable — fall through
  }
  return null;
}

/** Returns a React node for the message text, or null if only tool parts. */
function getMessageContent(
  msg: Record<string, unknown>,
  hasToolParts: boolean
): React.ReactNode {
  if (typeof msg.content === "string" && msg.content) {
    return <span>{msg.content}</span>;
  }

  if (typeof msg.text === "string" && msg.text) {
    return <span>{msg.text}</span>;
  }

  const textFromParts = extractTextFromParts(msg.parts);
  if (textFromParts) {
    return <span>{textFromParts}</span>;
  }

  const textFromContent = extractTextFromParts(msg.content);
  if (textFromContent) {
    return <span>{textFromContent}</span>;
  }

  if (hasToolParts) {
    return null;
  }

  return (
    <span className="text-destructive text-xs italic">
      Unrenderable message
    </span>
  );
}

/**
 * Pure derivation: for a given tool part, return the PendingAction that
 * should render as an inline card — or null if none matches. Correlation
 * is by toolCallId, mirroring the effect-cards-by-toolCallId pattern.
 *
 * Rendering policy: an action renders a card iff it has a `pcTargetId`
 * (i.e. some PC ever owed a roll on it). The kernel sets this on the
 * awaiting_roll transition and preserves it through commit, so the
 * predicate covers both the interactive-pending case and the committed
 * historical-record case without inspecting outcomes.
 *
 * Generic over the pending-action shape so callers can use the full
 * PendingAction type without coupling this helper to that schema.
 */
type DerivableAction = {
  toolCallId: string;
  data?: {
    pcTargetId?: string | null;
    awaitingTargetId?: string | null;
  };
};

export function deriveCardForToolPart<T extends DerivableAction>(
  pendingActions: readonly T[],
  toolCallId: string
): T | null {
  const match = pendingActions.find((a) => a.toolCallId === toolCallId);
  if (!match) {
    return null;
  }
  // pcTargetId is the canonical field; awaitingTargetId is the pre-pcTargetId
  // fallback for rows written before this field existed.
  const pcTarget = match.data?.pcTargetId ?? match.data?.awaitingTargetId;
  return pcTarget ? match : null;
}

/**
 * Pure derivation: for a given tool-startEncounter part, return the PC
 * queue slots that should render as inline InitiativeRollCards. Anchors
 * by toolCallId (mirrors deriveCardForToolPart's correlation model).
 *
 * Rendering policy:
 *  - No cards if there's no active encounter.
 *  - No cards if this tool part did not produce the current encounter
 *    (toolCallId mismatch with startEncounterToolCallId).
 *  - Otherwise, return the queue slots whose actorId is classified as a
 *    PC by the caller-supplied `isPc` predicate. When `myPlayerEntityId`
 *    is set (multiplayer), narrow further to only that player's entity.
 *    When null (single-player / shared device), return all PC slots.
 *
 * Generic over the `isPc` predicate so the frontend owns PC classification
 * without this helper needing an entity-state lookup.
 */
export function deriveInitiativeCardsForToolPart(
  encounter: EncounterDomain | null,
  toolCallId: string,
  myPlayerEntityId: string | null,
  isPc: (actorId: string) => boolean
): Array<{ actorId: string; initiative: number | null }> {
  if (!encounter?.encounterActive) {
    return [];
  }
  if (encounter.startEncounterToolCallId !== toolCallId) {
    return [];
  }
  const pcSlots = encounter.queue.filter((q) => isPc(q.actorId));
  if (myPlayerEntityId) {
    return pcSlots.filter((q) => q.actorId === myPlayerEntityId);
  }
  return pcSlots;
}

/**
 * Pure derivation: per-message indicator entries — one per non-skill-check
 * tool part — pairing the part with its EffectCards (filtered by toolCallId)
 * and a flag for whether the icon/name/error row should be drawn.
 *
 * Read against `allToolParts` (NOT the suppressed `displayToolParts`) so
 * EffectCards survive the streaming-time suppression that hides duplicate
 * indicators. Without this, after a player roll the cards anchored to the
 * source `execute_pending_action` tool part vanish until streaming ends —
 * see LAF-88. The icon row keeps the suppression because the streaming
 * overlay already shows that progress; the cards have no overlay equivalent.
 *
 * Skill-check parts are filtered out — they render their own card downstream
 * of the message text, not in the indicator strip.
 *
 * Empty entries (suppressed indicator AND no cards) are dropped so the JSX
 * doesn't render empty wrappers.
 */
export function selectIndicatorEntriesForMessage(opts: {
  toolParts: readonly ToolInvocationPart[];
  effectCards: readonly EffectCardData[];
  suppressIndicators: boolean;
}): Array<{
  tp: ToolInvocationPart;
  showIndicatorRow: boolean;
  cards: EffectCardData[];
}> {
  const showIndicatorRow = !opts.suppressIndicators;
  return opts.toolParts
    .filter((tp) => toolPartName(tp) !== "forge_skill_check")
    .map((tp) => ({
      tp,
      showIndicatorRow,
      cards: opts.effectCards.filter((c) => c.toolCallId === tp.toolCallId),
    }))
    .filter((entry) => entry.showIndicatorRow || entry.cards.length > 0);
}

/** ms to wait after streaming ends before clearing the handoff phase */
const HANDOFF_GRACE_MS = 300;

export function CampaignChat({
  campaignId,
  token,
  pendingChoiceResolutionNonce = 0,
}: {
  campaignId: string;
  token?: string;
  pendingChoiceResolutionNonce?: number;
}) {
  const [initialMessages, setInitialMessages] = useState<UIMessage[] | null>(
    null
  );
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [historyError, setHistoryError] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const { authDisabled, fetchWithAuth } = useAuthToken();

  // biome-ignore lint/correctness/useExhaustiveDependencies: reloadNonce is an intentional trigger dependency — clicking Retry increments it to re-run the fetch
  useEffect(() => {
    let mounted = true;

    async function fetchHistory() {
      if (!token && !authDisabled) {
        setIsLoadingHistory(false);
        setInitialMessages([]);
        return;
      }

      try {
        const res = await fetchWithAuth(`/api/campaigns/${campaignId}/history`);
        if (!res.ok) {
          // A 401 here is an EXPIRED token the retry could not refresh, or a
          // genuine auth failure — NOT an empty campaign. Surface it.
          throw new Error(`Failed to load history (${res.status})`);
        }
        const data = await res.json();

        if (mounted) {
          setHistoryError(false);
          setInitialMessages(data.messages || []);
        }
      } catch (err) {
        logger.error({ err }, "Failed to fetch campaign history");
        if (mounted) {
          setHistoryError(true);
          setInitialMessages(null); // keep it null: error ≠ empty campaign
        }
      } finally {
        if (mounted) {
          setIsLoadingHistory(false);
        }
      }
    }
    fetchHistory();
    return () => {
      mounted = false;
    };
  }, [campaignId, token, authDisabled, fetchWithAuth, reloadNonce]);

  if (historyError && !isLoadingHistory) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground italic">
        <span>Could not load the campaign records.</span>
        <Button
          onClick={() => {
            setHistoryError(false);
            setIsLoadingHistory(true);
            setReloadNonce((n) => n + 1);
          }}
          type="button"
          variant="outline"
        >
          Retry
        </Button>
      </div>
    );
  }

  if (isLoadingHistory || initialMessages === null) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground italic">
        <Loader2 className="h-6 w-6 animate-spin" />
        Loading records from the Sovereign Kernel...
      </div>
    );
  }

  return (
    <SkillCheckAnimationProvider>
      <CampaignChatInner
        campaignId={campaignId}
        initialMessages={initialMessages}
        key={campaignId}
        pendingChoiceResolutionNonce={pendingChoiceResolutionNonce}
      />
    </SkillCheckAnimationProvider>
  );
}

function CampaignChatInner({
  campaignId,
  initialMessages,
  pendingChoiceResolutionNonce,
}: {
  campaignId: string;
  initialMessages: UIMessage[];
  pendingChoiceResolutionNonce: number;
}) {
  const NUDGE_TEXT = "The dice have been cast.";
  const nudgeSentRef = useRef(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  // Content wrapper that holds all rendered messages. We observe this
  // element with ResizeObserver rather than walking Radix's internal
  // DOM via `viewport.firstElementChild`, which would be fragile to
  // Radix version upgrades.
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // Tracks whether the user is pinned to the bottom of the chat viewport.
  // Updated by the scroll listener effect on real user scroll events
  // (content growth does not fire scroll events, so this reflects intent).
  // Initialised true so the first render scrolls to the bottom on mount
  // with existing history.
  const pinnedToBottomRef = useRef(true);
  const [inputValue, setInputValue] = useState("");
  const gameState = useGameState();
  const { getToken } = useAuthToken();

  // ── Legacy SSE tool event state ────────────────────────────────────
  // These events are captured directly from the SSE stream via the
  // transport callback, bypassing the AI SDK's batching. This gives us
  // reliable, granular React state updates for each tool lifecycle event.
  const [toolEvents, setToolEvents] = useState<ToolStatusEvent[]>([]);
  const [handoffPhase, setHandoffPhase] = useState(false);

  // ── Firebase Hosting CDN bypass ─────────────────────────────────────
  // Firebase Hosting buffers SSE responses before forwarding to the browser.
  // We bypass this by calling the Cloud Run service URL directly for the SSE
  // stream, using a Firebase ID token instead of the session cookie.
  // The streaming URL is injected into the HTML by the Fastify server as
  // window.__VERDEREM_CONFIG__ so it's available immediately (no async race).
  // Falls back to /api/config for local dev where Vite serves the HTML.
  const streamingUrlRef = useRef<string | undefined>(
    window.__VERDEREM_CONFIG__?.streamingUrl
  );

  // A genuine tool ERROR is a player-facing failure (the action did NOT
  // happen), not debug noise — surface it by default so a failed move/tool
  // can't be silently papered over by the narrator's prose. The config flag
  // remains an explicit escape hatch to suppress it.
  const showToolErrors = window.__VERDEREM_CONFIG__?.showToolErrors ?? true;

  useEffect(() => {
    if (!streamingUrlRef.current) {
      fetch("/api/config")
        .then((res) => res.json())
        .then((cfg: { streamingUrl?: string }) => {
          if (cfg.streamingUrl) {
            streamingUrlRef.current = cfg.streamingUrl;
          }
        })
        .catch((err) =>
          logger.warn({ err }, "Failed to fetch streaming config")
        );
    }
  }, []);

  const getStreamingUrl = useCallback(() => streamingUrlRef.current, []);

  const getIdToken = useCallback(async () => {
    return (await getToken()) ?? undefined;
  }, [getToken]);

  const handleToolEvent = useCallback(
    (event: ToolStatusEvent) => {
      setToolEvents((prev) => [...prev, event]);
      if (event.type === "tool_complete" && event.invalidates) {
        gameState.invalidate([...event.invalidates]);
      }
    },
    [gameState]
  );

  const agentChatTransport = useRef(
    createToolAwareTransport(handleToolEvent, {
      getStreamingUrl,
      getIdToken,
    })
  );

  const { messages, sendMessage, status, error, setMessages } =
    useChat<UIMessage>({
      id: campaignId,
      transport: agentChatTransport.current,
      // @ts-expect-error - The Vercel AI SDK types this differently across versions
      initialMessages,
    });

  // Manually sync initialMessages if useChat fails to pick them up
  useEffect(() => {
    if (initialMessages.length > 0 && messages.length === 0) {
      setMessages(initialMessages);
    }
  }, [initialMessages, messages.length, setMessages]);

  const isLoading = status === "submitted" || status === "streaming";

  const handleNudge = useCallback(async () => {
    if (nudgeSentRef.current || isLoading) {
      return;
    }
    nudgeSentRef.current = true;
    try {
      gameState.invalidate(["skill-checks"]);
      await sendMessage(
        {
          role: "user",
          parts: [{ type: "text", text: NUDGE_TEXT }],
        },
        { body: { campaignId } }
      );
    } catch (err) {
      nudgeSentRef.current = false;
      logger.error({ err }, "Nudge sendMessage failed");
    }
  }, [isLoading, gameState, sendMessage, campaignId]);

  useEffect(() => {
    if (pendingChoiceResolutionNonce <= 0) {
      return;
    }
    handleNudge();
  }, [handleNudge, pendingChoiceResolutionNonce]);

  // Completed tool events (now delivered progressively via padded SSE flush)
  const completedToolEvents = toolEvents.filter(
    (e) => e.type === "tool_complete"
  );

  // Failed tool events with error text — used in both streaming UI and error banner
  const failedToolEvents = useMemo(
    () => completedToolEvents.filter((e) => e.ok === false && e.errorText),
    [completedToolEvents]
  );

  // Determine the currently active tool (started but not yet completed)
  const activeTool = useMemo(() => {
    const lastStart = [...toolEvents]
      .reverse()
      .find((e) => e.type === "tool_start");
    if (!lastStart) {
      return null;
    }
    const hasCompleted = toolEvents.some(
      (e) =>
        e.type === "tool_complete" &&
        e.tool === lastStart.tool &&
        e.timestamp > lastStart.timestamp
    );
    return hasCompleted ? null : lastStart;
  }, [toolEvents]);

  // "Streaming UI" = actual streaming OR handoff phase
  const showStreamingUI = isLoading || handoffPhase;

  // Debug mode: append ?debug to URL to show tool part inspector
  const showDebugParts =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("debug");

  // ── Handoff phase: bridge the gap between streaming end and reveal ─
  // Enter handoff when streaming ends with pending tool events
  useEffect(() => {
    if (!isLoading && toolEvents.length > 0 && !handoffPhase) {
      setHandoffPhase(true);
    }
  }, [isLoading, toolEvents.length, handoffPhase]);

  // Exit handoff after all completed events are revealed + grace period
  const exitHandoff = useCallback(() => {
    if (!error) {
      setToolEvents([]);
    }
    setHandoffPhase(false);
  }, [error]);

  useEffect(() => {
    if (!handoffPhase) {
      return;
    }

    const timer = setTimeout(exitHandoff, HANDOFF_GRACE_MS);
    return () => clearTimeout(timer);
  }, [handoffPhase, exitHandoff]);

  // Reset nudgeSentRef once the assistant has responded
  useEffect(() => {
    if (!nudgeSentRef.current) {
      return;
    }
    const lastMsg = messages.at(-1);
    if (lastMsg?.role === "assistant" && !isLoading) {
      nudgeSentRef.current = false;
    }
  }, [messages, isLoading]);

  // Scroll behaviour: track user intent via scroll events, auto-scroll
  // via ResizeObserver on the content wrapper so we re-anchor to the
  // bottom whenever content grows (initial history load, SSE streaming,
  // async skill-check card hydration).
  //
  // Why ResizeObserver instead of `[messages]` deps: a plain messages
  // effect fires once per reference change, but it runs before async
  // child content (like SkillCheckCard fetching its data) has fully
  // rendered. The scroll lands too high. ResizeObserver catches every
  // growth event — from the initial history render, from streaming
  // tokens, AND from cards expanding after their data arrives.
  //
  // Why the scroll listener stays separate: content growth does not
  // fire scroll events, so only real user scrolls update the intent ref.
  //
  // Assumes scrollRef is stable for the lifetime of the mounted component
  // (no conditional remount of ScrollArea — see spec).
  useEffect(() => {
    if (!scrollRef.current) {
      return;
    }
    const viewport = scrollRef.current.querySelector(
      "[data-radix-scroll-area-viewport]"
    );
    if (!viewport) {
      return;
    }
    const content = messagesContainerRef.current;
    if (!content) {
      return;
    }

    const scrollToBottom = () => {
      viewport.scrollTop = viewport.scrollHeight;
    };

    const handleScroll = () => {
      const distanceFromBottom =
        viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
      pinnedToBottomRef.current = distanceFromBottom <= AUTOSCROLL_THRESHOLD_PX;
    };

    const resizeObserver = new ResizeObserver(() => {
      if (pinnedToBottomRef.current) {
        scrollToBottom();
      }
    });

    viewport.addEventListener("scroll", handleScroll, { passive: true });
    resizeObserver.observe(content);

    // Initial scroll for history load — fires before the observer would
    // have measured anything, ensuring the first paint starts at the bottom.
    scrollToBottom();

    return () => {
      viewport.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
    };
  }, []);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) {
      return;
    }

    const message = inputValue;
    setInputValue("");

    // Reset streaming UI state for new interaction
    setToolEvents([]);
    setHandoffPhase(false);
    nudgeSentRef.current = false;

    try {
      await sendMessage(
        {
          role: "user",
          parts: [{ type: "text", text: message }],
        },
        {
          body: {
            campaignId,
          },
        }
      );
    } catch (err) {
      logger.error({ err }, "Failed to send message");
    }
  };

  const handleComposerKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) {
      return;
    }
    e.preventDefault();
    e.currentTarget.form?.requestSubmit();
  };

  // The last assistant message ID — used to suppress native tool parts
  // during streaming/handoff (they'd duplicate the legacy event indicators)
  const lastAssistantMsgId = [...messages]
    .reverse()
    .find((m) => m.role === "assistant")?.id;

  // Mutual-exclusion set for Phase 1 (live indicator) card rendering.
  // A toolCallId in this set is already owned by a visible AI-SDK tool part
  // (Phase 2), so Phase 1 must NOT render cards for it — that would duplicate.
  // Mirrors the per-message suppressToolParts logic below: the active
  // assistant message contributes nothing while streaming/handoff is in play.
  const displayedToolCallIds = useMemo(() => {
    const ids = new Set<string>();
    for (const m of messages) {
      if (m.role !== "assistant") {
        continue;
      }
      const suppress = showStreamingUI && m.id === lastAssistantMsgId;
      if (suppress) {
        continue;
      }
      const msgWithParts = m as UIMessageWithParts;
      const parts: unknown[] = Array.isArray(msgWithParts.parts)
        ? msgWithParts.parts
        : [];
      for (const p of parts) {
        if (isToolPart(p)) {
          ids.add(p.toolCallId);
        }
      }
    }
    return ids;
  }, [messages, showStreamingUI, lastAssistantMsgId]);

  return (
    <div className="flex h-full flex-col">
      <InitiativeHeader />
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="space-y-4" ref={messagesContainerRef}>
          {messages.length === 0 && !isLoading && (
            <div className="py-8 text-center text-muted-foreground text-xs italic">
              No message history found.
            </div>
          )}
          {messages.map((m) => {
            // Hide nudge messages from the UI
            if (m.role === "user") {
              // messageText handles both shapes: live `parts`-shaped messages
              // and `content`-only history messages (the nudge replayed from
              // buildUiHistory). See components/message-text.ts.
              const nudgeText = messageText(m);
              if (nudgeText === NUDGE_TEXT) {
                return null;
              }
            }

            const msgWithParts = m as UIMessageWithParts;
            const parts: unknown[] = Array.isArray(msgWithParts.parts)
              ? msgWithParts.parts
              : [];
            const allToolParts = parts.filter(isToolPart);

            // Suppress the icon/name indicator rows on the active message
            // during streaming/handoff to avoid duplicating the legacy
            // event-driven indicators in the streaming overlay. EffectCards
            // are *not* suppressed — see LAF-88 — because they have no
            // streaming-time alternative and would otherwise vanish after a
            // player roll until a new assistant message lands.
            const suppressToolParts =
              showStreamingUI && m.id === lastAssistantMsgId;
            const displayToolParts = suppressToolParts ? [] : allToolParts;
            // Skill-check parts render AFTER the message text (narrative
            // context first). Reads `allToolParts` (not displayToolParts)
            // for the same reason as indicatorEntries: suppression unmounts
            // SkillCheckCard mid-flow when the LLM nudges back into a stream
            // after resolution, causing the card to vanish until the new
            // assistant message lands. See LAF-88.
            const skillCheckParts = allToolParts.filter(
              (tp) => toolPartName(tp) === "forge_skill_check"
            );
            // Indicator entries pair non-skill-check parts with their
            // EffectCards. Reads `allToolParts` (not displayToolParts) so
            // cards survive suppression. See selectIndicatorEntriesForMessage.
            const indicatorEntries = selectIndicatorEntriesForMessage({
              toolParts: allToolParts,
              effectCards: gameState.effectCards,
              suppressIndicators: suppressToolParts,
            });
            const hasIndicatorEntries = indicatorEntries.length > 0;
            const hasSkillCheckParts = skillCheckParts.length > 0;

            // Hide text on the active message during streaming/handoff
            // (text appears after all tool checkmarks have been revealed)
            const shouldHideText = suppressToolParts && toolEvents.length > 0;
            const messageContent = getMessageContent(
              msgWithParts as unknown as Record<string, unknown>,
              allToolParts.length > 0
            );

            return (
              <div
                className={cn(
                  "flex min-w-0 max-w-[80%] flex-col gap-1",
                  m.role === "user"
                    ? "ml-auto items-end"
                    : "mr-auto items-start"
                )}
                key={m.id}
              >
                {/* Debug: show part info when ?debug is in the URL */}
                {showDebugParts &&
                  allToolParts.length > 0 &&
                  m.role === "assistant" && (
                    <div className="mb-1 rounded border border-yellow-500/30 bg-yellow-500/5 p-2 font-mono text-[10px] text-yellow-400">
                      <div>
                        parts: {allToolParts.length} | suppressed:{" "}
                        {String(suppressToolParts)} | display:{" "}
                        {displayToolParts.length}
                      </div>
                      {allToolParts.map((tp, i) => {
                        let outStr: string;
                        try {
                          outStr =
                            typeof tp.output === "object"
                              ? JSON.stringify(tp.output).slice(0, 80)
                              : String(tp.output ?? "nil");
                        } catch {
                          outStr = "[unstringifiable]";
                        }
                        return (
                          <div key={tp.toolCallId ?? i}>
                            [{i}] type={tp.type} name={tp.toolName ?? "?"}{" "}
                            state={tp.state} out={outStr}
                          </div>
                        );
                      })}
                    </div>
                  )}
                {/* Tool invocation indicators (native parts — for completed/historical messages) */}
                {hasIndicatorEntries && m.role === "assistant" && (
                  <div className="flex flex-col gap-1 text-muted-foreground text-xs">
                    {indicatorEntries.map(({ tp, showIndicatorRow, cards }) => {
                      const name = toolPartName(tp);
                      const isExecuting =
                        tp.state === "input-available" ||
                        tp.state === "input-streaming";
                      const isError = tp.state === "output-error";
                      return (
                        <div
                          className="flex flex-col gap-1"
                          key={tp.toolCallId}
                        >
                          {showIndicatorRow && (
                            <div className="flex items-center gap-1.5">
                              {isExecuting ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : isError ? (
                                <X className="h-3 w-3 text-red-500/70" />
                              ) : (
                                <Check className="h-3 w-3 text-green-500/70" />
                              )}
                              <span className={isError ? "line-through" : ""}>
                                {formatToolName(name)}
                              </span>
                            </div>
                          )}
                          {showIndicatorRow && isError && showToolErrors && (
                            <div className="ml-4 space-y-1 font-mono text-xs">
                              {tp.input !== undefined && (
                                <div className="rounded bg-destructive/10 p-2">
                                  <div className="mb-1 font-semibold text-destructive">
                                    Args
                                  </div>
                                  <pre className="whitespace-pre-wrap break-all">
                                    {JSON.stringify(tp.input, null, 2)}
                                  </pre>
                                </div>
                              )}
                              {tp.errorText && (
                                <div className="rounded bg-destructive/10 p-2">
                                  <div className="mb-1 font-semibold text-destructive">
                                    Error
                                  </div>
                                  <pre className="whitespace-pre-wrap break-all">
                                    {tp.errorText}
                                  </pre>
                                </div>
                              )}
                            </div>
                          )}
                          {cards.length > 0 && (
                            <div
                              className="mt-1 flex flex-col gap-2"
                              data-testid="effect-cards-stack"
                            >
                              {cards.map((c) => (
                                <EffectCard
                                  card={c}
                                  key={`${c.toolCallId}-${c.sequenceNumber}`}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Message text content — hidden during streaming/handoff for the active message */}
                {messageContent && !shouldHideText && (
                  <div
                    className={cn(
                      "max-w-full whitespace-pre-wrap break-words rounded-lg px-4 py-2 text-sm [overflow-wrap:anywhere]",
                      m.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary text-secondary-foreground"
                    )}
                  >
                    {messageContent}
                  </div>
                )}
                {/* Skill check cards render AFTER narrative text so the player reads context first */}
                {hasSkillCheckParts && m.role === "assistant" && (
                  <div className="flex flex-col gap-2">
                    {skillCheckParts.map((tp) => {
                      const scId = extractSkillCheckId(tp);
                      if (scId) {
                        return (
                          <SkillCheckCard
                            campaignId={campaignId}
                            key={tp.toolCallId}
                            onResolved={handleNudge}
                            skillCheckId={scId}
                          />
                        );
                      }
                      // Fallback indicator (forge in-flight, no skillCheckId
                      // yet) duplicates the streaming overlay's tool-status
                      // line. Suppress on the active streaming message; the
                      // SkillCheckCard branch above is the only thing we want
                      // to render through suppression. See LAF-88 review.
                      if (suppressToolParts) {
                        return null;
                      }
                      const isStreaming =
                        tp.state === "input-available" ||
                        tp.state === "input-streaming";
                      return (
                        <div
                          className="flex items-center gap-1.5 text-muted-foreground text-xs"
                          key={tp.toolCallId}
                        >
                          {isStreaming ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Check className="h-3 w-3 text-green-500/70" />
                          )}
                          <span>{formatToolName(toolPartName(tp))}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Inline ActionRollCard: anchored to the tool call that produced the pending action */}
                {m.role === "assistant" &&
                  allToolParts
                    .filter(
                      (tp) => toolPartName(tp) === "execute_pending_action"
                    )
                    .map((tp) => {
                      const fullAction = deriveCardForToolPart(
                        gameState.pendingActions,
                        tp.toolCallId
                      );
                      if (!fullAction) {
                        return null;
                      }
                      return (
                        <ActionRollCard
                          action={fullAction}
                          campaignId={campaignId}
                          key={tp.toolCallId}
                          onResolved={handleNudge}
                        />
                      );
                    })}
                {/* Inline InitiativeRollCard: anchored to the tool-startEncounter part */}
                {m.role === "assistant" &&
                  allToolParts
                    .filter((tp) => toolPartName(tp) === "start_encounter")
                    .flatMap((tp) => {
                      const cards = deriveInitiativeCardsForToolPart(
                        gameState.encounter,
                        tp.toolCallId,
                        null, // myPlayerEntityId — single-player v1 renders all PC slots
                        // v1 isPc predicate: we don't have a direct PC marker on the queue
                        // shape post-resolve. Render cards for every queue slot; NPC slots
                        // carry numeric initiative and InitiativeRollCard renders them in
                        // resolved state with no button. The only user-interactive button
                        // appears for null slots — always PCs by invariant §2. Follow-up:
                        // add `controller` to QueueItem when multiplayer PC-mapping lands.
                        () => true
                      );
                      return cards.map((slot) => (
                        <InitiativeRollCard
                          actorId={slot.actorId}
                          campaignId={campaignId}
                          initiative={slot.initiative}
                          key={`${tp.toolCallId}:${slot.actorId}`}
                          onResolved={handleNudge}
                        />
                      ));
                    })}
                <span className="px-1 text-[10px] text-muted-foreground uppercase">
                  {m.role}
                </span>
              </div>
            );
          })}

          {/* Streaming indicator: legacy event-driven tool progress */}
          {showStreamingUI && (
            <div className="mr-auto flex max-w-[80%] flex-col gap-2 text-muted-foreground text-sm italic">
              {/* Progressively revealed completed tools */}
              {completedToolEvents.length > 0 && (
                <div className="flex flex-col gap-1 text-xs">
                  {completedToolEvents.map((e) => {
                    // Phase 1 effect cards: render under the indicator unless
                    // Phase 2 has already claimed this toolCallId (mutual-exclusion).
                    // Skip if the event lacks a toolCallId (legacy / lifecycle-info).
                    const hasToolCallId =
                      typeof e.toolCallId === "string" &&
                      e.toolCallId.length > 0;
                    const alreadyInDisplayParts =
                      hasToolCallId &&
                      e.toolCallId !== undefined &&
                      displayedToolCallIds.has(e.toolCallId);
                    const shouldRenderCards =
                      hasToolCallId && !alreadyInDisplayParts;
                    const cards = shouldRenderCards
                      ? gameState.effectCards.filter(
                          (c) => c.toolCallId === e.toolCallId
                        )
                      : [];
                    return (
                      <div
                        className="flex animate-tool-fade-in flex-col gap-1"
                        key={`${e.tool}-${e.timestamp}`}
                      >
                        <div className="flex items-center gap-1.5">
                          {e.ok !== false ? (
                            <Check className="h-3 w-3 text-green-500/70" />
                          ) : (
                            <X className="h-3 w-3 text-red-500/70" />
                          )}
                          <span
                            className={e.ok === false ? "line-through" : ""}
                          >
                            {formatToolName(e.tool)}
                            {e.detail ? ` (${e.detail})` : ""}
                          </span>
                        </div>
                        {e.ok === false && showToolErrors && e.errorText && (
                          <div className="ml-4 font-mono text-xs">
                            <div className="rounded bg-destructive/10 p-2">
                              <div className="mb-1 font-semibold text-destructive">
                                Error
                              </div>
                              <pre className="whitespace-pre-wrap break-all">
                                {e.errorText}
                              </pre>
                            </div>
                          </div>
                        )}
                        {cards.length > 0 && (
                          <div
                            className="mt-1 flex flex-col gap-2"
                            data-testid="effect-cards-stack-phase1"
                          >
                            {cards.map((c) => (
                              <EffectCard
                                card={c}
                                key={`${c.toolCallId}-${c.sequenceNumber}`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Spinner — only during actual streaming, not handoff */}
              {isLoading && (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {activeTool
                    ? `${formatToolName(activeTool.tool)}${
                        activeTool.detail ? ` (${activeTool.detail})` : ""
                      }...`
                    : "Agent is thinking..."}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="mx-auto flex flex-col gap-2 rounded bg-destructive/10 px-4 py-2 text-destructive text-sm italic">
              <div>
                {error instanceof ChatSDKError && error.type === "rate_limit"
                  ? error.message
                  : `Failed to connect to Agent: ${error.message}`}
              </div>
              {showToolErrors && failedToolEvents.length > 0 && (
                <div className="space-y-1 font-mono text-xs not-italic">
                  <div className="font-semibold">
                    Tool errors before disconnect:
                  </div>
                  {failedToolEvents.map((e) => (
                    <div
                      className="rounded bg-destructive/20 p-2"
                      key={`err-${e.tool}-${e.timestamp}`}
                    >
                      <span className="font-semibold">
                        {formatToolName(e.tool)}:
                      </span>{" "}
                      <pre className="inline whitespace-pre-wrap break-all">
                        {e.errorText}
                      </pre>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Pinned turn-blocking ambient saves: rendered above the composer so a
          gated player can't scroll past the save that's blocking their actions.
          Sourced from the saveRolls store slice (worker-forged ⇒ no tool call to
          anchor to), keyed by saveRoll.id. The gate (Slice 1) is the real
          enforcement; this is discovery + focus. */}
      {gameState.saveRolls
        .filter((sr) => sr.metadata?.turnBlocking === true)
        .map((sr) => (
          <div className="mx-4 mt-2" key={sr.id}>
            <SaveRollCard
              campaignId={campaignId}
              onResolved={() => {
                gameState.invalidate(["save-rolls"]);
                handleNudge();
              }}
              saveRoll={sr}
            />
          </div>
        ))}

      {gameState.pendingChoices
        .filter((choice) => choice.status === "pending")
        .map((choice) => (
          <div className="mx-4 mt-2" key={choice.entityId}>
            <PendingChoiceCard
              campaignId={campaignId}
              choice={choice}
              onResolved={handleNudge}
            />
          </div>
        ))}

      <Card className="m-4 border-t-0 p-2 shadow-lg">
        <form
          autoComplete="off"
          className="flex items-end gap-2"
          onSubmit={handleFormSubmit}
        >
          <Textarea
            aria-label="Message to the Game Master"
            autoCapitalize="sentences"
            autoComplete="off"
            className="max-h-32 min-h-10 flex-1 resize-none border-none py-2 focus-visible:ring-0"
            disabled={isLoading}
            enterKeyHint="send"
            inputMode="text"
            name="verderem-chat-composer"
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleComposerKeyDown}
            placeholder="Type your message to the Game Master..."
            rows={1}
            spellCheck={true}
            value={inputValue}
          />
          <Button
            className="h-9 w-9 shrink-0"
            disabled={isLoading || !inputValue.trim()}
            size="icon"
            type="submit"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </form>
      </Card>
    </div>
  );
}
